/**
 * Music theory helpers for the score (audio-direction §4.4): the region's mode and a seeded tonic and tempo,
 * scale degrees to MIDI notes (degrees wrap into octaves, so motifs transpose in-mode), and MIDI to Hz. Pure.
 */
import type { MusicConfig } from './musicSchema'

export interface Key {
  readonly region: string
  /** Tonic pitch class (0 = C). */
  readonly root: number
  /** Scale intervals (semitones above the tonic). */
  readonly mode: readonly number[]
  readonly modeName: string
  readonly bpm: number
}

function hashPick(seed: number, salt: number, n: number): number {
  let h = (Math.imul(seed ^ salt, 0x9e3779b1) ^ (seed >>> 13)) >>> 0
  h = Math.imul(h ^ (h >>> 15), 0x85ebca77) >>> 0
  return ((h ^ (h >>> 13)) >>> 0) % n
}

/** The key and tempo of `region` for `seed` (the system seed varies them within the region's palette). */
export function keyFor(cfg: MusicConfig, region: string, seed: number): Key {
  const r = cfg.regions[region]
  if (r === undefined) throw new Error(`unknown music region ${region}`)
  const mode = cfg.modes[r.mode]
  const steps = r.tempo[1] - r.tempo[0]
  return { region, root: r.roots[hashPick(seed, 0x51, r.roots.length)], mode, modeName: r.mode, bpm: r.tempo[0] + hashPick(seed, 0x7e, steps + 1) }
}

/** MIDI note of scale `degree` (0-based, may be negative or past the octave) in octave `octave` (C4 = 60). */
export function degreeMidi(key: Key, degree: number, octave: number): number {
  const n = key.mode.length
  const wrap = Math.floor(degree / n)
  const d = degree - wrap * n
  return 12 * (octave + 1 + wrap) + key.root + key.mode[d]
}

export function midiHz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12)
}

/** Whether `midi` belongs to the key's scale. */
export function inKey(key: Key, midi: number): boolean {
  const pc = (((midi - key.root) % 12) + 12) % 12
  return key.mode.includes(pc)
}
